import {
  doc,
  updateDoc,
  increment,
  collection,
  query,
  where,
  limit,
  getDocs,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'

// Simple in-memory rate limiting (for client-side protection)
const rateLimitMap = new Map<string, { count: number; resetTime: number }>()
const RATE_LIMIT_WINDOW = 60000 // 1 minute
const MAX_REQUESTS_PER_WINDOW = 5

export interface PromoCode {
  code: string
  remainingUses: number
  expirationDate: string | Timestamp // ISO date string or Firestore Timestamp
  id?: string
}

export interface PromoCodeValidationResult {
  isValid: boolean
  error?: string
  promoCode?: PromoCode
}

/**
 * Validates a promo code against the promoCodes collection
 */
export const validatePromoCode = async (code: string): Promise<PromoCodeValidationResult> => {
  // Convert code to uppercase for consistent validation
  const normalizedCode = code.toUpperCase()

  // Rate limiting check
  const now = Date.now()
  const clientId = 'anonymous' // In a real app, you might use IP or session ID

  const rateLimit = rateLimitMap.get(clientId)
  if (rateLimit) {
    if (now > rateLimit.resetTime) {
      // Reset window
      rateLimitMap.set(clientId, { count: 1, resetTime: now + RATE_LIMIT_WINDOW })
    } else if (rateLimit.count >= MAX_REQUESTS_PER_WINDOW) {
      return {
        isValid: false,
        error: 'Too many validation attempts. Please try again in a minute.',
      }
    } else {
      // Increment count
      rateLimit.count++
    }
  } else {
    // First request
    rateLimitMap.set(clientId, { count: 1, resetTime: now + RATE_LIMIT_WINDOW })
  }

  try {
    // Query the promoCodes collection to find the document with matching code
    const promoCodesRef = collection(db, 'promoCodes')
    const q = query(promoCodesRef, where('code', '==', normalizedCode), limit(1))
    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      return {
        isValid: false,
        error: 'Invalid promo code',
      }
    }

    const promoCodeDoc = querySnapshot.docs[0]
    const promoCode = promoCodeDoc.data() as PromoCode
    const currentDate = new Date()

    // Handle Firestore Timestamp
    let expirationDate: Date
    if (promoCode.expirationDate instanceof Timestamp) {
      // Firestore Timestamp
      expirationDate = promoCode.expirationDate.toDate()
    } else {
      // String date
      expirationDate = new Date(promoCode.expirationDate)
    }

    // Check if code has expired
    if (expirationDate < currentDate) {
      return {
        isValid: false,
        error: 'Sorry, this promo code is expired.',
      }
    }

    // Check if code has remaining uses
    if (promoCode.remainingUses <= 0) {
      return {
        isValid: false,
        error: 'Sorry, this promo code has been fully used.',
      }
    }

    return {
      isValid: true,
      promoCode: {
        ...promoCode,
        id: promoCodeDoc.id,
      },
    }
  } catch (error) {
    console.error('Error validating promo code:', error)
    return {
      isValid: false,
      error: 'Error validating promo code',
    }
  }
}

/**
 * Decrements the remaining uses for a promo code
 */
export const decrementPromoCodeUses = async (code: string): Promise<void> => {
  try {
    const normalizedCode = code.toUpperCase()

    // Find the document with the matching code
    const promoCodesRef = collection(db, 'promoCodes')
    const q = query(promoCodesRef, where('code', '==', normalizedCode), limit(1))
    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      throw new Error('Promo code not found')
    }

    const promoCodeDoc = querySnapshot.docs[0]
    await updateDoc(doc(db, 'promoCodes', promoCodeDoc.id), {
      remainingUses: increment(-1),
    })
  } catch (error) {
    console.error('Error using promo code:', error)
    throw new Error('Failed to use promo code')
  }
}
